import { FC, SVGProps, useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import {
  matchPath,
  NavLinkProps,
  useLocation,
  useParams,
} from 'react-router-dom';

import { env } from '@/config/env';
import { tKeys } from '@/i18n';

import { Gamezone } from '@/assets/icons/Olybet/Gamezone';
import { LobbyIcon } from '@/assets/icons/Olybet/Lobby.icon';
import {
  LeaderboardIcon,
  PencilIcon,
  PieChartIcon,
} from '@/assets/icons/components';

import { RoutesEnum } from '@/enums/routes.enum';

import { addIdToRoute } from '@/routes/util';

import { useContests } from './useContests.hook';
import { useSettings } from './useSettings';

interface IHeaderNavigation {
  label: string;
  Icon?: FC<SVGProps<any>>;
  selected: boolean;
  linkData: NavLinkProps;
}

export const useAppNav = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { contests, currentContest } = useContests();
  const { lang } = useSettings();

  const currentId = (id ||
    currentContest?.contest_id ||
    contests?.[0]?.contest_id) as string;

  const createLinkToGZ = useCallback(
    (pathname?: string | undefined) => {
      if (pathname) {
        return `${env.GAMEZONE_URL}/${pathname}?lang=${lang}&redirectURL=${env.GAMEZONE_URL}/${pathname}`;
      }
      return `${env.GAMEZONE_URL}?lang=${lang}&redirectURL=${env.GAMEZONE_URL}`;
    },
    [lang],
  );

  const linkToGZ = createLinkToGZ();

  const sidebarNav = useMemo<IHeaderNavigation[]>(
    () => [
      {
        label: t(tKeys.returnToGZ),
        Icon: Gamezone,
        selected: false,
        linkData: {
          to: linkToGZ,
        },
      },
    ],
    [t, linkToGZ],
  );

  const contestNav = useMemo<IHeaderNavigation[]>(
    () => [
      {
        label: t(tKeys.navPicks),
        icon: PencilIcon,
        selected: pathname === addIdToRoute(RoutesEnum.play, currentId),
        linkData: {
          to: addIdToRoute(RoutesEnum.play, currentId),
        },
      },
      {
        label: t(tKeys.navLeaderboards),
        icon: LeaderboardIcon,
        selected: pathname === addIdToRoute(RoutesEnum.leaderboard, currentId),
        linkData: {
          to: addIdToRoute(RoutesEnum.leaderboard, currentId),
        },
      },
      {
        label: t(tKeys.breakdown),
        icon: PieChartIcon,
        selected: pathname === addIdToRoute(RoutesEnum.weeklyStats, currentId),
        linkData: {
          to: addIdToRoute(RoutesEnum.weeklyStats, currentId),
        },
      },
    ],
    [t, pathname, currentId],
  );

  const isContestNavVisible =
    pathname === addIdToRoute(RoutesEnum.play, currentId) ||
    pathname === addIdToRoute(RoutesEnum.leaderboard, currentId) ||
    pathname === addIdToRoute(RoutesEnum.weeklyStats, currentId);

  const mobileNav = useMemo<IHeaderNavigation[]>(
    () => [
      {
        label: t(tKeys.lobby),
        Icon: LobbyIcon,
        selected: pathname === RoutesEnum.home,
        linkData: {
          to: RoutesEnum.home,
        },
      },
      {
        label: t(tKeys.navGameZone),
        Icon: Gamezone,
        selected: false,
        linkData: {
          to: linkToGZ,
        },
      },
    ],
    [linkToGZ, pathname, t],
  );

  // Uses to show Back button
  const isSecondaryPage = useMemo(
    () =>
      [
        RoutesEnum.play,
        RoutesEnum.leaderboard,
        RoutesEnum.weeklyStats,
        RoutesEnum.rulesContest,
        RoutesEnum.rules,
        RoutesEnum.profile,
      ].some((route) => matchPath(route, pathname)),
    [pathname],
  );

  return {
    contestNav,
    isContestNavVisible,
    isSecondaryPage,
    linkToGZ,
    mobileNav,
    sidebarNav,
  };
};
